import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../../../core-components/theme/color-palette/colors';
import { ChoiceChipGroup } from '../../../../core-components/theme/components/Fields';
import { InlineAlert } from '../../../../core-components/theme/components/InlineAlert';
import { LANGUAGE_NAMES } from '../../../../i18n/languages';
import type { LanguageChoice } from '../../../../i18n/languages';
import { languageNoteText } from '../../../account';
import { liveLineSx, stepBodySx } from '../../constants/layout';
import { useOnboardingLanguage } from '../../hooks/useOnboardingLanguage';
import type { OnboardingLanguage } from '../../hooks/useOnboardingLanguage';
import { StepFrame } from '../StepFrame';
import type { WizardNav } from '../StepFrame';

const SAMPLES: Readonly<Record<LanguageChoice, string>> = {
  en: 'Alerts and this app in English',
  hi: 'अलर्ट और यह ऐप हिंदी में',
};

const optionsOf = (languages: readonly LanguageChoice[]) =>
  languages.map((value) => ({
    value,
    label: <span lang={value}>{LANGUAGE_NAMES[value]}</span>,
    description: <span lang={value}>{SAMPLES[value]}</span>,
  }));

const noteSx = { color: Neutrals[600] } as const;

const LanguageNoteLine: React.FC<{ language: OnboardingLanguage }> = ({ language }) => {
  const { t } = useTranslation();
  const text = languageNoteText(t, language.note);
  if (text === null) return null;
  return (
    <Typography variant="caption2" component="p" sx={noteSx}>
      {text}
    </Typography>
  );
};

const statusText = (t: TFunction, language: OnboardingLanguage): string | null => {
  switch (language.status) {
    case 'paused':
      return t('common.waitingForConnection', 'Waiting for the connection to come back…');
    case 'saving':
      return t('coreComponents.saveBar.saving', 'Saving…');
    case 'saved':
      return t('onboarding.language.saved', 'Saved — alerts will come in {{language}}', { language: LANGUAGE_NAMES[language.shown] });
    case 'idle':
    case 'failed':
      return null;
  }
};

const LanguageStatus: React.FC<{ language: OnboardingLanguage }> = ({ language }) => {
  const { t } = useTranslation();
  return (
    <>
      <Typography role="status" variant="label" component="p" sx={liveLineSx}>
        {statusText(t, language)}
      </Typography>
      {language.status === 'failed' && (
        <InlineAlert
          severity="error"
          role="alert"
          message={t('onboarding.language.failed', 'Couldn’t save your language. Pick it again to retry.')}
        />
      )}
    </>
  );
};

export const LanguageStep: React.FC<{ nav: WizardNav }> = ({ nav }) => {
  const { t } = useTranslation();
  const language = useOnboardingLanguage();
  return (
    <StepFrame
      nav={nav}
      title={t('onboarding.title.language', 'Language')}
      description={t('onboarding.language.help', 'Pick the language for your alerts and this app. You can change it anytime.')}
      primary={{
        label: t('onboarding.continue', 'Continue'),
        onPress: () => language.continueWith(nav.next),
        busy: language.status === 'saving' || language.status === 'paused',
      }}
    >
      <Box sx={stepBodySx}>
        <Box aria-busy={language.status === 'saving'}>
          <ChoiceChipGroup<LanguageChoice>
            label={t('onboarding.language.label', 'Language')}
            hideLabel
            columns={1}
            value={language.shown}
            onChange={language.choose}
            options={optionsOf(language.options)}
          />
        </Box>
        <LanguageNoteLine language={language} />
        <LanguageStatus language={language} />
      </Box>
    </StepFrame>
  );
};
